import { useCallback, useEffect, useState } from "react";
import Cropper from "react-easy-crop";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { useI18n } from "@/context/I18nContext";

/**
 * Square (1:1) crop dialog for athlete / judge photos.
 *
 * Usage:
 *   <PhotoCropDialog
 *     file={pendingFile}                // File | null (null => closed)
 *     onCancel={() => setPendingFile(null)}
 *     onConfirm={(croppedFile) => { ... }}
 *   />
 *
 * The output File keeps the original MIME (image/png stays PNG, everything
 * else becomes JPEG at 92% quality — good balance for portraits on the
 * Big Screen).
 */

// Read the file into a blob URL (NOT a data URL — base64 in a data URL costs
// ~3× the binary size in RAM, which is what triggers "Memory insufficient"
// on iOS Safari after two or three successive 10-megapixel shots).
const makeObjectURL = (file) => URL.createObjectURL(file);

// Draw the selected pixel area onto an off-screen canvas and export it as a
// Blob. area is what react-easy-crop returns in croppedAreaPixels.
const cropImageToBlob = async (imageSrc, area, mime) => {
  const img = new Image();
  img.crossOrigin = "anonymous";
  await new Promise((resolve, reject) => {
    img.onload = resolve;
    img.onerror = reject;
    img.src = imageSrc;
  });

  // Cap output to 1024x1024 — plenty for a 400px portrait on the Big Screen
  // and prevents ~10MB inputs from producing huge crops.
  // Cap output to 512x512 — more than enough for a 400 px portrait on
  // the Big Screen, and halves the canvas memory footprint vs 1024
  // (critical on Samsung Chrome Android which OOMs faster than iOS).
  const OUTPUT = Math.min(512, Math.max(area.width, 128));

  const canvas = document.createElement("canvas");
  canvas.width = OUTPUT;
  canvas.height = OUTPUT;
  const ctx = canvas.getContext("2d");

  ctx.drawImage(
    img,
    area.x, area.y, area.width, area.height,
    0, 0, OUTPUT, OUTPUT,
  );

  // Drop the decoded raster from the source Image right away.
  img.src = "";

  const blob = await new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error("crop-failed"))),
      mime,
      mime === "image/png" ? undefined : 0.92,
    );
  });

  // Explicitly release the canvas backing store — Safari won't do this on its
  // own fast enough and three successive crops exhaust its memory ceiling.
  canvas.width = 0;
  canvas.height = 0;

  return blob;
};

export const PhotoCropDialog = ({ file, onCancel, onConfirm }) => {
  const { t } = useI18n();
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [areaPixels, setAreaPixels] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!file) { setImageSrc(null); return undefined; }
    const url = makeObjectURL(file);
    setImageSrc(url);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setAreaPixels(null);
    // Revoke the blob URL when the file changes or the dialog closes — this
    // is the key leak fix for the "Memory insufficient" alert on iOS Safari
    // AND Samsung Chrome. Revoking frees the compressed source (~1 MB) that
    // react-easy-crop kept pinned via its <img>.
    return () => {
      try { URL.revokeObjectURL(url); } catch { /* no-op */ }
    };
  }, [file]);

  // Final safety net: when the dialog closes, drop every piece of state
  // that could keep a bitmap alive.
  useEffect(() => {
    if (!file) {
      setImageSrc(null);
      setAreaPixels(null);
      setZoom(1);
      setCrop({ x: 0, y: 0 });
    }
  }, [file]);

  const onCropComplete = useCallback((_area, pixels) => {
    setAreaPixels(pixels);
  }, []);

  const handleConfirm = async () => {
    if (!areaPixels || !imageSrc || !file) return;
    setBusy(true);
    // Capture refs locally and tear the Cropper down IMMEDIATELY so
    // react-easy-crop releases the ~16 MB source bitmap it keeps alive.
    // This is the key fix for Samsung Chrome's "Memory insufficient" alert.
    const area = areaPixels;
    const src = imageSrc;
    setImageSrc(null);
    try {
      // Fall back to JPEG when the input had no MIME (Samsung Chrome camera).
      const mime = (file.type === "image/png") ? "image/png" : "image/jpeg";
      const blob = await cropImageToBlob(src, area, mime);
      const ext = mime === "image/png" ? ".png" : ".jpg";
      const base = (file.name || "photo").replace(/\.[^.]+$/, "");
      const croppedFile = new File([blob], `${base}${ext}`, { type: mime });
      onConfirm(croppedFile);
    } catch (err) {
      // Silent failure here was the mobile UX trap. Surface it and fall
      // back to the raw file so the user doesn't lose their shot.
      // eslint-disable-next-line no-console
      console.error("photo crop failed", err);
      try { onConfirm(file); } catch (_) {}
    } finally {
      setBusy(false);
    }
  };

  const open = !!file;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !busy) onCancel(); }}>
      <DialogContent className="bg-background text-foreground border-white/20 sm:max-w-lg" data-testid="photo-crop-dialog">
        <DialogHeader>
          <DialogTitle className="text-foreground text-2xl">
            {t("crop.title") || "Adjust photo"}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {t("crop.hint") || "Drag to reposition, use the slider to zoom."}
          </DialogDescription>
        </DialogHeader>

        <div className="relative w-full aspect-square bg-black rounded-lg overflow-hidden" data-testid="photo-crop-canvas">
          {imageSrc && (
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={1}
              cropShape="round"
              showGrid={false}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
              restrictPosition
              zoomSpeed={0.4}
            />
          )}
        </div>

        <div className="flex items-center gap-3 py-2">
          <span className="text-xs text-muted-foreground w-10">
            {t("crop.zoom") || "Zoom"}
          </span>
          <input
            type="range"
            min={1}
            max={4}
            step={0.05}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            className="flex-1 accent-primary"
            data-testid="photo-crop-zoom"
          />
          <span className="text-xs text-muted-foreground w-10 text-right">
            {zoom.toFixed(1)}x
          </span>
        </div>

        <p className="text-xs text-muted-foreground italic">
          {t("crop.hint") || "Drag to reposition, use the slider to zoom."}
        </p>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={onCancel}
            disabled={busy}
            data-testid="photo-crop-cancel"
          >
            {t("common.cancel") || "Cancel"}
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={busy || !areaPixels}
            data-testid="photo-crop-confirm"
          >
            {busy ? (t("crop.processing") || "Processing…") : (t("crop.confirm") || "Use this crop")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PhotoCropDialog;
